import { clampBridgeString } from './clampBridgeString';
import { getShellBridge } from './shellBridge';
import { hasShellCapability, isInShell, useShellInfo } from './shellPresence';

export type ShareLinkData = { url: string; text?: string; title?: string };

// The shell's `share.open` schema caps; it drops the whole message over either.
const SHARE_TEXT_MAX_LENGTH = 1000;
const SHARE_TITLE_MAX_LENGTH = 200;

const hasWebShare = (): boolean => typeof navigator.share === 'function';

/** Whether the shell opens its native share sheet — Android WebView has no `navigator.share`. */
export function canShareInShell(): boolean {
  return isInShell() && hasShellCapability('share');
}

/** Whether a share button can do anything here: the Web Share API, or the shell's share sheet. */
export function canShareLink(): boolean {
  return hasWebShare() || canShareInShell();
}

/** {@link canShareLink}, re-rendering when the shell confirms. */
export function useCanShareLink(): boolean {
  const info = useShellInfo();
  return hasWebShare() || hasShellCapability('share', info);
}

/**
 * Shares through the Web Share API when the browser has it, otherwise through the shell (`share.open`).
 * @returns Whether a share sheet was opened; rejects as `navigator.share` does (e.g. `AbortError`).
 */
export async function shareLink({ url, text, title }: ShareLinkData): Promise<boolean> {
  if (hasWebShare()) {
    await navigator.share({ url, text, title });
    return true;
  }

  const bridge = canShareInShell() ? getShellBridge() : null;
  if (bridge === null) return false;

  const absoluteUrl = new URL(url, window.location.origin).href;
  bridge.send('share.open', {
    url: absoluteUrl,
    ...(text && { text: clampBridgeString(text, SHARE_TEXT_MAX_LENGTH) }),
    ...(title && { title: clampBridgeString(title, SHARE_TITLE_MAX_LENGTH) }),
  });
  return true;
}
